/**
 * v3.21-1624 · Unknown Inbound Caller pane.
 *
 * Auto-opens when the WebSocket `incoming_call` event has NO client_id
 * (unknown number).  Never creates a fake client.  Uses the same SID
 * as the authoritative telephony session so ActiveCallPill keeps
 * showing the same call.
 *
 * Provides:
 *   • Number + Unknown Caller indicator + ringing/active state
 *   • Working-Note textarea (persisted to server-side note draft
 *     under call_sid scope, with client_id=null)
 *   • Find/Link Client search that hits `/api/clients?search=` and,
 *     on selection, PATCHes the underlying call log to link the SID
 *     to the client + opens the standard ClientWorkspaceModal for
 *     that client with the same callSid attached.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { X, Loader2, PhoneIncoming, User, Link as LinkIcon, Send } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useClientWorkspace } from '../context/ClientWorkspaceContext';

const LS_UNKNOWN = 'unknownCaller.open.v1';

export default function UnknownCallerModal() {
  const { openClientWorkspace } = useClientWorkspace();

  const [state, setState]     = useState(() => {
    try {
      const raw = window.localStorage.getItem(LS_UNKNOWN);
      if (!raw) return { open: false, callSid: null, phone: null };
      const p = JSON.parse(raw);
      if (p?.callSid) return { open: true, ...p };
    } catch { /* noop */ }
    return { open: false, callSid: null, phone: null };
  });
  const [note, setNote]       = useState('');
  const [q, setQ]             = useState('');
  const [results, setResults] = useState([]);
  const [linking, setLinking] = useState(false);
  const noteDraftIdRef = useRef(null);

  // Persist open-state so refresh/PWA-reload restores it.
  useEffect(() => {
    try {
      if (state.open && state.callSid) {
        window.localStorage.setItem(LS_UNKNOWN, JSON.stringify({
          callSid: state.callSid, phone: state.phone,
        }));
      } else {
        window.localStorage.removeItem(LS_UNKNOWN);
      }
    } catch { /* noop */ }
  }, [state.open, state.callSid, state.phone]);

  // Global event bridge — any WS handler can fire this to surface the pane.
  useEffect(() => {
    const onOpen  = (e) => {
      const { callSid, phone } = e.detail || {};
      if (!callSid) return;
      setState({ open: true, callSid, phone: phone || 'Unknown' });
    };
    const onClose = () => setState({ open: false, callSid: null, phone: null });
    window.addEventListener('unknown-caller:open',  onOpen);
    window.addEventListener('unknown-caller:close', onClose);
    return () => {
      window.removeEventListener('unknown-caller:open',  onOpen);
      window.removeEventListener('unknown-caller:close', onClose);
    };
  }, []);

  // Search for clients as the rep types.
  useEffect(() => {
    if (!state.open) return undefined;
    const term = (q || '').trim();
    if (term.length < 2) { setResults([]); return undefined; }
    const h = setTimeout(async () => {
      try {
        const r = await api.get('/clients', { params: { search: term, limit: 10 } });
        const rows = Array.isArray(r.data) ? r.data : (r.data?.clients || []);
        setResults(rows);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(h);
  }, [q, state.open]);

  // Autosave note as a server draft (client_id=null legal only when
  // draft store accepts it — currently requires client, so we skip
  // server draft in unknown mode and use localStorage only).
  useEffect(() => {
    if (!state.callSid) return;
    try {
      window.localStorage.setItem(
        `unknownCaller.note:${state.callSid}`,
        note,
      );
    } catch { /* noop */ }
  }, [note, state.callSid]);

  // Restore note on mount.
  useEffect(() => {
    if (!state.callSid) return;
    try {
      const v = window.localStorage.getItem(`unknownCaller.note:${state.callSid}`);
      if (v) setNote(v);
    } catch { /* noop */ }
  }, [state.callSid]);

  const linkTo = useCallback(async (client) => {
    if (!client?.id || !state.callSid) return;
    setLinking(true);
    try {
      // Best-effort: update the call log to associate the client id.
      // If the endpoint is missing we still proceed with the UI link.
      try {
        await api.post(`/telephony/calls/${state.callSid}/link-client`, {
          client_id: client.id,
        });
      } catch { /* endpoint may not exist; UI link still works */ }

      // Migrate the WIP note into the client's server-side draft.
      if (note && note.trim()) {
        try {
          await api.post('/note-drafts', {
            client_id: client.id,
            call_sid:  state.callSid,
            content:   note.trim(),
          });
        } catch { /* noop */ }
      }

      // Open the STANDARD ClientWorkspace with the linked call.
      openClientWorkspace(client.id, { callSid: state.callSid });
      toast.success(`Linked call to ${client.full_name || client.email || 'client'}`);

      // Clear the unknown pane.
      try { window.localStorage.removeItem(`unknownCaller.note:${state.callSid}`); } catch { /* noop */ }
      setState({ open: false, callSid: null, phone: null });
      setNote(''); setQ(''); setResults([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to link caller');
    } finally { setLinking(false); }
  }, [state.callSid, note, openClientWorkspace]);

  const close = () => setState({ open: false, callSid: null, phone: null });
  if (!state.open || !state.callSid) return null;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent
        className="max-w-md p-0 gap-0"
        data-testid="unknown-caller-modal"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-3 py-2 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2 text-sm">
            <PhoneIncoming className="h-4 w-4 text-emerald-700 animate-pulse" />
            <span className="font-semibold">Incoming Call</span>
            <span className="text-slate-500">·</span>
            <span className="font-mono text-slate-800" data-testid="unknown-caller-phone">
              {state.phone || 'Unknown'}
            </span>
            <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid="unknown-caller-badge">
              Unknown Caller
            </span>
            <Button type="button" size="sm" variant="ghost" className="ml-auto h-7 w-7 p-0"
              onClick={close} data-testid="unknown-caller-close-btn">
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription className="text-[11px] text-slate-500">
            No matching client on file for this number.  Take notes and link to a client when you know who they are.
          </DialogDescription>
        </DialogHeader>

        <div className="p-3 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
              <User className="h-3 w-3" /> Note about this call
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="What are they calling about?"
              className="mt-1 w-full text-sm rounded-md border border-slate-300 px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500"
              data-testid="unknown-caller-note-input"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
              <LinkIcon className="h-3 w-3" /> Find / Link Client
            </label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name / email / phone…"
              className="mt-1 w-full text-sm rounded-md border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
              data-testid="unknown-caller-search"
            />
            <div className="mt-1 max-h-48 overflow-y-auto space-y-1">
              {q.trim().length >= 2 && results.length === 0 && (
                <div className="text-xs text-slate-500 py-1" data-testid="unknown-caller-empty">
                  No matching clients — do NOT create a fake client here.
                </div>
              )}
              {results.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  disabled={linking}
                  onClick={() => linkTo(c)}
                  className="w-full text-left rounded px-2 py-1 hover:bg-emerald-50 border border-slate-100 disabled:opacity-50"
                  data-testid={`unknown-caller-pick-${c.id}`}
                >
                  <div className="text-sm font-medium truncate">{c.full_name || 'Client'}</div>
                  <div className="text-[10px] text-slate-500 truncate">{c.email} · {c.phone}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
